// Static lookup of the 54 weekly Torah portions (parashot). Each entry is
// the canonical starting reference. We resolve start/end letter offsets at
// load time against the verse metadata produced by the index builder.
//
// Names are kept in transliterated English for now (they sort cleanly and
// fit narrow heatmap columns); the Hebrew name is provided as `nameHebrew`
// and shown in tooltips.

import type { VerseMeta } from "./types";

export interface ParashaRef {
  name: string;
  nameHebrew: string;
  book: string;
  chapter: number;
  verse: number;
}

export const PARASHOT: ReadonlyArray<ParashaRef> = [
  { name: "Bereshit", nameHebrew: "בְּרֵאשִׁית", book: "Genesis", chapter: 1, verse: 1 },
  { name: "Noach", nameHebrew: "נֹחַ", book: "Genesis", chapter: 6, verse: 9 },
  { name: "Lech-Lecha", nameHebrew: "לֶךְ־לְךָ", book: "Genesis", chapter: 12, verse: 1 },
  { name: "Vayera", nameHebrew: "וַיֵּרָא", book: "Genesis", chapter: 18, verse: 1 },
  { name: "Chayei Sara", nameHebrew: "חַיֵּי שָׂרָה", book: "Genesis", chapter: 23, verse: 1 },
  { name: "Toldot", nameHebrew: "תּוֹלְדֹת", book: "Genesis", chapter: 25, verse: 19 },
  { name: "Vayetze", nameHebrew: "וַיֵּצֵא", book: "Genesis", chapter: 28, verse: 10 },
  { name: "Vayishlach", nameHebrew: "וַיִּשְׁלַח", book: "Genesis", chapter: 32, verse: 4 },
  { name: "Vayeshev", nameHebrew: "וַיֵּשֶׁב", book: "Genesis", chapter: 37, verse: 1 },
  { name: "Miketz", nameHebrew: "מִקֵּץ", book: "Genesis", chapter: 41, verse: 1 },
  { name: "Vayigash", nameHebrew: "וַיִּגַּשׁ", book: "Genesis", chapter: 44, verse: 18 },
  { name: "Vayechi", nameHebrew: "וַיְחִי", book: "Genesis", chapter: 47, verse: 28 },

  { name: "Shemot", nameHebrew: "שְׁמוֹת", book: "Exodus", chapter: 1, verse: 1 },
  { name: "Vaera", nameHebrew: "וָאֵרָא", book: "Exodus", chapter: 6, verse: 2 },
  { name: "Bo", nameHebrew: "בֹּא", book: "Exodus", chapter: 10, verse: 1 },
  { name: "Beshalach", nameHebrew: "בְּשַׁלַּח", book: "Exodus", chapter: 13, verse: 17 },
  { name: "Yitro", nameHebrew: "יִתְרוֹ", book: "Exodus", chapter: 18, verse: 1 },
  { name: "Mishpatim", nameHebrew: "מִשְׁפָּטִים", book: "Exodus", chapter: 21, verse: 1 },
  { name: "Terumah", nameHebrew: "תְּרוּמָה", book: "Exodus", chapter: 25, verse: 1 },
  { name: "Tetzaveh", nameHebrew: "תְּצַוֶּה", book: "Exodus", chapter: 27, verse: 20 },
  { name: "Ki Tisa", nameHebrew: "כִּי תִשָּׂא", book: "Exodus", chapter: 30, verse: 11 },
  { name: "Vayakhel", nameHebrew: "וַיַּקְהֵל", book: "Exodus", chapter: 35, verse: 1 },
  { name: "Pekudei", nameHebrew: "פְקוּדֵי", book: "Exodus", chapter: 38, verse: 21 },

  { name: "Vayikra", nameHebrew: "וַיִּקְרָא", book: "Leviticus", chapter: 1, verse: 1 },
  { name: "Tzav", nameHebrew: "צַו", book: "Leviticus", chapter: 6, verse: 1 },
  { name: "Shemini", nameHebrew: "שְּׁמִינִי", book: "Leviticus", chapter: 9, verse: 1 },
  { name: "Tazria", nameHebrew: "תַזְרִיעַ", book: "Leviticus", chapter: 12, verse: 1 },
  { name: "Metzora", nameHebrew: "מְּצֹרָע", book: "Leviticus", chapter: 14, verse: 1 },
  { name: "Acharei Mot", nameHebrew: "אַחֲרֵי מוֹת", book: "Leviticus", chapter: 16, verse: 1 },
  { name: "Kedoshim", nameHebrew: "קְדֹשִׁים", book: "Leviticus", chapter: 19, verse: 1 },
  { name: "Emor", nameHebrew: "אֱמֹר", book: "Leviticus", chapter: 21, verse: 1 },
  { name: "Behar", nameHebrew: "בְּהַר", book: "Leviticus", chapter: 25, verse: 1 },
  { name: "Bechukotai", nameHebrew: "בְּחֻקֹּתַי", book: "Leviticus", chapter: 26, verse: 3 },

  { name: "Bamidbar", nameHebrew: "בְּמִדְבַּר", book: "Numbers", chapter: 1, verse: 1 },
  { name: "Naso", nameHebrew: "נָשֹׂא", book: "Numbers", chapter: 4, verse: 21 },
  { name: "Beha'alotcha", nameHebrew: "בְּהַעֲלֹתְךָ", book: "Numbers", chapter: 8, verse: 1 },
  { name: "Sh'lach", nameHebrew: "שְׁלַח־לְךָ", book: "Numbers", chapter: 13, verse: 1 },
  { name: "Korach", nameHebrew: "קֹרַח", book: "Numbers", chapter: 16, verse: 1 },
  { name: "Chukat", nameHebrew: "חֻקַּת", book: "Numbers", chapter: 19, verse: 1 },
  { name: "Balak", nameHebrew: "בָּלָק", book: "Numbers", chapter: 22, verse: 2 },
  { name: "Pinchas", nameHebrew: "פִּינְחָס", book: "Numbers", chapter: 25, verse: 10 },
  { name: "Matot", nameHebrew: "מַּטּוֹת", book: "Numbers", chapter: 30, verse: 2 },
  { name: "Masei", nameHebrew: "מַסְעֵי", book: "Numbers", chapter: 33, verse: 1 },

  { name: "Devarim", nameHebrew: "דְּבָרִים", book: "Deuteronomy", chapter: 1, verse: 1 },
  { name: "Va'etchanan", nameHebrew: "וָאֶתְחַנַּן", book: "Deuteronomy", chapter: 3, verse: 23 },
  { name: "Eikev", nameHebrew: "עֵקֶב", book: "Deuteronomy", chapter: 7, verse: 12 },
  { name: "Re'eh", nameHebrew: "רְאֵה", book: "Deuteronomy", chapter: 11, verse: 26 },
  { name: "Shoftim", nameHebrew: "שֹׁפְטִים", book: "Deuteronomy", chapter: 16, verse: 18 },
  { name: "Ki Teitzei", nameHebrew: "כִּי תֵצֵא", book: "Deuteronomy", chapter: 21, verse: 10 },
  { name: "Ki Tavo", nameHebrew: "כִּי תָבוֹא", book: "Deuteronomy", chapter: 26, verse: 1 },
  { name: "Nitzavim", nameHebrew: "נִצָּבִים", book: "Deuteronomy", chapter: 29, verse: 9 },
  { name: "Vayeilech", nameHebrew: "וַיֵּלֶךְ", book: "Deuteronomy", chapter: 31, verse: 1 },
  { name: "Ha'azinu", nameHebrew: "הַאֲזִינוּ", book: "Deuteronomy", chapter: 32, verse: 1 },
  { name: "V'Zot HaBerachah", nameHebrew: "וְזֹאת הַבְּרָכָה", book: "Deuteronomy", chapter: 33, verse: 1 },
];

export interface ParashaRange {
  ref: ParashaRef;
  // Inclusive start letter offset in the compact corpus.
  startCompact: number;
  // Exclusive end letter offset. The last parasha ends at N.
  endCompact: number;
}

export function buildParashaRanges(
  verses: VerseMeta[],
  N: number,
): ParashaRange[] {
  if (verses.length === 0) return [];
  const out: ParashaRange[] = [];
  for (const ref of PARASHOT) {
    const start = findVerseStart(verses, ref);
    if (start < 0) continue; // Skip silently if a parasha is unmapped.
    out.push({ ref, startCompact: start, endCompact: N });
  }
  // Walk again to fill in actual ends from the next parasha's start.
  for (let i = 0; i < out.length - 1; i++) {
    out[i]!.endCompact = out[i + 1]!.startCompact;
  }
  return out;
}

function findVerseStart(verses: VerseMeta[], ref: ParashaRef): number {
  for (const v of verses) {
    if (v.book === ref.book && v.chapter === ref.chapter && v.verse === ref.verse) {
      return v.compactStart;
    }
  }
  return -1;
}

// Binary search the parasha containing letter `p`.
export function findParashaAt(
  ranges: ParashaRange[],
  p: number,
): ParashaRange | null {
  if (ranges.length === 0) return null;
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const r = ranges[mid]!;
    if (p < r.startCompact) hi = mid - 1;
    else if (p >= r.endCompact) lo = mid + 1;
    else return r;
  }
  return null;
}
